import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useBadgeTemplates,
  useCreateBadgeTemplate,
  useUpdateBadgeTemplate,
  useDeleteBadgeTemplate,
  useBadgeTemplateById,
} from '../../../hooks/queries/useVisitorManagement';
import type {
  BadgeTemplate,
  BadgeTemplateWrite,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';

interface BadgeRow {
  [key: string]: unknown;
  id: string;
  name: string;
  size: string;
  is_default: boolean;
  is_active: boolean;
}

const columns: Column<BadgeRow>[] = [
  { key: 'name', label: 'Name', sortable: true, width: '200px' },
  { key: 'size', label: 'Size', width: '120px' },
  {
    key: 'is_default',
    label: 'Default',
    width: '100px',
    render: (row) =>
      row.is_default ? (
        <StatusBadge status={'active' as StatusType} label="Default" />
      ) : null,
  },
  {
    key: 'is_active',
    label: 'Active',
    width: '100px',
    render: (row) => (
      <StatusBadge
        status={
          row.is_active ? ('active' as StatusType) : ('inactive' as StatusType)
        }
        label={row.is_active ? 'Active' : 'Inactive'}
      />
    ),
  },
];

export function useBadgeTemplateCrud() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const isBadgeTemplateTab =
    urlTab?.toLowerCase().replace(/\s+/g, '-') === 'badge-templates';

  const { data, isLoading, error } = useBadgeTemplates();
  const { data: badgeTemplateById, error: badgeTemplateByIdError } =
    useBadgeTemplateById(urlId as string, isBadgeTemplateTab);
  const createMutation = useCreateBadgeTemplate();
  const updateMutation = useUpdateBadgeTemplate();
  const deleteMutation = useDeleteBadgeTemplate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<BadgeTemplate | null>(null);
  const isSingleBadgeTemplateView = Boolean(
    urlId && isBadgeTemplateTab && badgeTemplateById && !badgeTemplateByIdError,
  );
  const badgeTemplatesData = isSingleBadgeTemplateView
    ? badgeTemplateById
      ? [badgeTemplateById]
      : []
    : (data ?? []);
  const rows: BadgeRow[] = useMemo(
    () =>
      (badgeTemplatesData ?? []).map((b) => ({
        id: b.public_id,
        name: b.name,
        size: `${b.width_inches}" x ${b.height_inches}"`,
        is_default: b.is_default,
        is_active: b.is_active,
      })),
    [badgeTemplatesData],
  );

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: BadgeRow) => {
    const item = data?.find((b) => b.public_id === row.id);
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: BadgeTemplateWrite,
    setError: UseFormSetError<BadgeTemplateWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof BadgeTemplateWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: values },
        {
          onSuccess: () => {
            toaster.create({
              title: 'Badge template updated',
              type: 'success',
            });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          toaster.create({ title: 'Badge template created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Badge template deleted', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: BadgeTemplateWrite = useMemo(
    () =>
      editItem
        ? {
            name: editItem.name,
            is_default: editItem.is_default,
            width_inches: editItem.width_inches,
            height_inches: editItem.height_inches,
            is_active: editItem.is_active,
          }
        : {
            name: '',
            is_default: false,
            width_inches: 2.25,
            height_inches: 4,
            is_active: true,
          },
    [editItem],
  );

  return {
    data: badgeTemplatesData,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  };
}
